import keysService from "api/keysService";
import NotaIcon from "assets/icons/nota.svg?react";
import Identity from "components/Activation/Identity";
import RecoveryPackage from "components/Activation/RecoveryPackage";
import HollowButton from "components/HollowButton";
import LoadingScreen from "components/LoadingScreen";
import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getUserIdentityVerified } from "utils/getUserIdentityVerified";
import { getUserIsActivated } from "utils/getUserIsActivated";
import styles from "./styles.module.scss";
import { getUserClaim } from "utils/getUserClaim";
import { getUserId } from "utils/getUserId";
import { clearAllKeys } from "utils/clearAllKeys";
import { Claims } from "dto/User/claims";
import AuthLayout from "components/AuthLayout";
import { ESteps } from "components/Stepper";
import WelcomeBack from "components/Activation/WelcomeBack";
import ReactGA from "react-ga4";
import { useMediaQuery } from "usehooks-ts";

export enum ActivationSteps {
  RecoveryKey = "recoveryKey",
  Identity = "identity",
  WelcomeBack = "welcomeBack",
}

const ActivationModule = () => {
  const { t } = useTranslation();

  const NormalPersonSteps = [
    {
      type: ESteps.INFORMATION,
      title: t("activation.stepper.accountInfo.title"),
      text: t("activation.stepper.accountInfo.description"),
    },
    {
      type: ESteps.EMAIL_VERIFICATION,
      title: t("activation.stepper.emailVerification.title"),
      text: t("activation.stepper.emailVerification.description"),
    },
    {
      type: ESteps.ID_VERIFICATION,
      title: t("activation.stepper.identity.title"),
      text: t("activation.stepper.identity.description"),
    },
    {
      type: ESteps.KEY_GENERATION,
      title: t("activation.stepper.recoveryKey.title"),
      text: t("activation.stepper.recoveryKey.description"),
    },
  ];

  const guestSteps = [
    {
      type: ESteps.INFORMATION,
      title: t("activation.stepper.accountInfo.title"),
      text: t("activation.stepper.accountInfo.description"),
    },
    {
      type: ESteps.KEY_GENERATION,
      title: t("activation.stepper.recoveryKey.title"),
      text: t("activation.stepper.recoveryKey.description"),
    },
  ];

  const auth = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState<ESteps>(ESteps.ID_VERIFICATION);
  const [steps, setSteps] = useState([]);
  const isMobile = useMediaQuery("(max-width: 767px)");

  const {
    data: recoveryPackage,
    isLoading: isLoadingRecoveryPackage,
    isSuccess: isSuccessRecoveryPackage,
  } = keysService.useGetRecoveryPackage();

  const [step, setStep] = useState<string | null>(null);

  const sendGaEvent = (action: string, label: string, params?: any) => {
    try {
      ReactGA.event(
        {
          category: "activation",
          action: action,
          label: label, // optional
          transport: "xhr", // optional, beacon/xhr/image
        },
        params,
      );
    } catch {}
  };

  const handleNextStep = () => {
    switch (step) {
      case ActivationSteps.Identity:
        sendGaEvent("activation_identity", "Zweryfikowano tożsamość");
        setS(ESteps.KEY_GENERATION);
        setStep(ActivationSteps.RecoveryKey);
        break;
      case ActivationSteps.RecoveryKey:
        sendGaEvent("activation_keygen", "Wygenerowano klucz przywracania");
        auth.signinSilent();
        break;
      case ActivationSteps.WelcomeBack:
        sendGaEvent("activation_resume", "Powrót do kreatora aktywacji");
        setS(ESteps.KEY_GENERATION);
        setStep(ActivationSteps.RecoveryKey);
        break;
    }
  };

  const navigateToMainPage = () => {
    clearAllKeys(getUserId(auth.user));
    auth.signoutRedirect();
    //navigate("/home");
  };

  useEffect(() => {
    if (isSuccessRecoveryPackage) {
      if (auth.user && getUserIdentityVerified(auth.user)) {
        setStep(ActivationSteps.RecoveryKey);
      } else {
        setStep(ActivationSteps.Identity);
      }
    } else {
      const userClaim = getUserClaim(auth.user!, Claims.invitation);
      if (userClaim?.length) {
        setStep(ActivationSteps.RecoveryKey);
        return;
      }

      const countryClaim = getUserClaim(auth.user!, Claims.Country);

      if (countryClaim?.length) {
        if (countryClaim.toUpperCase() !== "PL") {
          setStep(ActivationSteps.RecoveryKey);
          return;
        }
      }

      if (auth.user && getUserIdentityVerified(auth.user)) {
        setStep(ActivationSteps.WelcomeBack);
      } else {
        setStep(ActivationSteps.Identity);
      }
    }
  }, [recoveryPackage]);
  const isGuest = auth?.user?.profile?.role === "guest";
  //@ts-ignore
  const isInvitation = !!auth?.user?.profile?.invitation?.length ?? false;

  useEffect(() => {
    const newSteps = !isGuest && !isInvitation ? NormalPersonSteps : guestSteps;
    setSteps(newSteps);
  }, [auth.user]);

  useEffect(() => {
    if (isGuest) {
      setStep(ActivationSteps.RecoveryKey);
    }

    if (auth.user && getUserIsActivated(auth.user)) {
      navigate("/");
    }
  }, [auth.user]);

  useEffect(() => {
    if (isSuccessRecoveryPackage && isGuest) {
      navigate("/packages/1B8AE8D3-3209-497D-9526-27608FFFA84D/incoming");
    }
  }, [isSuccessRecoveryPackage]);

  if (isLoadingRecoveryPackage) {
    return <LoadingScreen></LoadingScreen>;
  }

  return (
    <AuthLayout
      actualStep={!isGuest ? s : 1}
      steps={steps}
      footerChildren={
        <div className={styles.footerLeft}>
          <HollowButton
            text={t("common.backHome")}
            onClick={() => navigateToMainPage?.()}
          ></HollowButton>
        </div>
      }
    >
      <>
        {step === ActivationSteps.RecoveryKey && (
          <RecoveryPackage
            nextStep={handleNextStep}
            hasRecoveryPackage={isSuccessRecoveryPackage}
          ></RecoveryPackage>
        )}
        {step === ActivationSteps.Identity && (
          <Identity nextStep={handleNextStep}></Identity>
        )}
        {step === ActivationSteps.WelcomeBack && (
          <WelcomeBack nextStep={handleNextStep}></WelcomeBack>
        )}

        {isMobile && (
          <div className={styles.ourlogo}>
            <NotaIcon></NotaIcon>
          </div>
        )}
      </>
    </AuthLayout>
  );
};
export default ActivationModule;
